import { MouseEvent } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import {
  EMPTY_SCORING_RULE_FILTERS,
  ScoringRuleFilters,
  StatusFilter,
  hasActiveFilters,
} from "./scoring-rule-filters";

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

const toggleSx = {
  "& .MuiToggleButton-root": {
    px: 1.5,
    py: 0.5,
    fontSize: "0.813rem",
    textTransform: "none",
    border: "1px solid",
    borderColor: "divider",
    "&.Mui-selected": {
      backgroundColor: "primary.main",
      color: "primary.contrastText",
      "&:hover": {
        backgroundColor: "primary.dark",
      },
    },
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
};

interface StatusToggleProps {
  label: string;
  value: StatusFilter;
  onChange: (value: StatusFilter) => void;
}

const StatusToggle = ({ label, value, onChange }: StatusToggleProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <ToggleButtonGroup
      value={value}
      exclusive
      size="small"
      aria-label={label}
      onChange={(
        _event: MouseEvent<HTMLElement>,
        next: StatusFilter | null,
      ) => {
        if (next !== null) onChange(next);
      }}
      sx={toggleSx}
    >
      {statusOptions.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);

interface ScoringRuleFilterBarProps {
  filters: ScoringRuleFilters;
  onChange: (filters: ScoringRuleFilters) => void;
  ruleTypes: string[];
  showClientStatus?: boolean;
  shownCount: number;
  totalCount: number;
}

const ScoringRuleFilterBar = ({
  filters,
  onChange,
  ruleTypes,
  showClientStatus = false,
  shownCount,
  totalCount,
}: ScoringRuleFilterBarProps) => {
  const update = (changes: Partial<ScoringRuleFilters>) =>
    onChange({ ...filters, ...changes });

  return (
    <Box
      sx={{
        mb: 2,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <TextField
        size="small"
        placeholder="Search rules"
        value={filters.search}
        onChange={(e) => update({ search: e.target.value })}
        slotProps={{
          input: {
            startAdornment: (
              <SearchIcon
                fontSize="small"
                sx={{ mr: 1, color: "text.secondary" }}
              />
            ),
          },
          htmlInput: { "aria-label": "Search rules" },
        }}
        sx={{ width: 220 }}
      />
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="rule-type-filter-label">Rule Type</InputLabel>
        <Select
          labelId="rule-type-filter-label"
          id="rule-type-filter"
          value={filters.ruleType}
          label="Rule Type"
          onChange={(e) => update({ ruleType: e.target.value })}
        >
          <MenuItem value="">All rule types</MenuItem>
          {ruleTypes.map((ruleType) => (
            <MenuItem key={ruleType} value={ruleType}>
              {ruleType}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <StatusToggle
        label="Rule status"
        value={filters.status}
        onChange={(status) => update({ status })}
      />
      {showClientStatus && (
        <StatusToggle
          label="Client status"
          value={filters.clientStatus}
          onChange={(clientStatus) => update({ clientStatus })}
        />
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          size="small"
          type="number"
          label="Min score"
          value={filters.minScore}
          onChange={(e) => update({ minScore: e.target.value })}
          sx={{ width: 110 }}
        />
        <Typography color="text.secondary">–</Typography>
        <TextField
          size="small"
          type="number"
          label="Max score"
          value={filters.maxScore}
          onChange={(e) => update({ maxScore: e.target.value })}
          sx={{ width: 110 }}
        />
      </Box>
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {shownCount} of {totalCount}
        </Typography>
        <Button
          size="small"
          startIcon={<FilterListOffIcon />}
          disabled={!hasActiveFilters(filters)}
          onClick={() => onChange(EMPTY_SCORING_RULE_FILTERS)}
        >
          Clear filters
        </Button>
      </Box>
    </Box>
  );
};

export default ScoringRuleFilterBar;
